import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTheme } from '@/context/theme.context';
import { APP_HEX } from '@/constant/theme/colors.constant';
import { Text } from '../atoms/AppText';

export interface SegmentedControlOption<T extends string | number = string> {
  value: T;
  label: string;
  /** Optional icon component (e.g. from react-icons/lu) */
  icon?: React.ElementType;
  /** Render only the icon — label used as aria title */
  iconOnly?: boolean;
  /** Disable this specific option */
  disabled?: boolean;
  /** Small badge / count shown after the label */
  badge?: string | number;
}

export interface SegmentedControlProps<T extends string | number = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * Visual size preset
   * - `xs`  ultra-compact for inline toolbars
   * - `sm`  compact
   * - `md`  default
   * - `lg`  spacious
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Stretch to fill parent width */
  fullWidth?: boolean;
  /**
   * Track shape
   * - `pill`     fully rounded (default)
   * - `rounded`  large radius
   * - `soft`     medium radius
   * - `square`   small radius
   */
  shape?: 'pill' | 'rounded' | 'soft' | 'square';
  /**
   * Thumb style
   * - `gradient`  theme gradient fill (default)
   * - `solid`     theme primary solid
   * - `white`     white / elevated card look
   * - `outline`   border only, no fill
   */
  variant?: 'gradient' | 'solid' | 'white' | 'outline';
  /** Extra classes on the outer track */
  className?: string;
  /** Disable the entire control */
  disabled?: boolean;
  /** data-testid for automation */
  testId?: string;
}

/* ─────────────────────────────────────────────────────────────────────────── */

const SegmentedControl = <T extends string | number = string>({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
  shape = 'pill',
  variant = 'gradient',
  className = '',
  disabled = false,
  testId,
}: SegmentedControlProps<T>) => {
  const { colors, isDark } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [thumb, setThumb] = useState<React.CSSProperties>({});
  const [mounted, setMounted] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);

  const activeIndex = options.findIndex((o) => o.value === value);

  const updateThumb = useCallback(() => {
    const container = containerRef.current;
    const activeBtn = itemRefs.current[activeIndex];
    if (!container || !activeBtn) return;
    const cr = container.getBoundingClientRect();
    const br = activeBtn.getBoundingClientRect();
    setThumb({ left: br.left - cr.left, width: br.width, height: br.height });
    setMounted(true);
  }, [activeIndex]);

  /* ── slide thumb ──────────────────────────────────────────────────────── */
  useLayoutEffect(() => {
    updateThumb();
  }, [updateThumb, options]);

  useEffect(() => {
    updateThumb();

    const onResize = () => updateThumb();
    window.addEventListener('resize', onResize);

    const container = containerRef.current;
    const activeBtn = itemRefs.current[activeIndex];
    let observer: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => updateThumb());
      if (container) observer.observe(container);
      if (activeBtn) observer.observe(activeBtn);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [activeIndex, updateThumb]);

  /* ── size tokens ──────────────────────────────────────────────────────── */
  const sizeMap = {
    xs: {
      pad: 'py-[3px] px-2',
      text: 'text-[9px] tracking-widest',
      icon: 'h-[10px] w-[10px]',
      gap: 'gap-[5px]',
      track: 'p-[2px]',
      badge: 'text-[8px] px-1 py-px min-w-[14px]',
      thumbTop: 2,
    },
    sm: {
      pad: 'py-1 px-2.5 sm:px-3',
      text: 'text-[10px] tracking-wider',
      icon: 'h-[11px] w-[11px]',
      gap: 'gap-1',
      track: 'p-[2px]',
      badge: 'text-[9px] px-1.5 py-px min-w-[16px]',
      thumbTop: 2,
    },
    md: {
      pad: 'py-1 px-2.5 sm:py-1.5 sm:px-4',
      text: 'text-[10px] sm:text-[11px] tracking-wide',
      icon: 'h-3 w-3',
      gap: 'gap-1 sm:gap-1.5',
      track: 'p-[3px]',
      badge: 'text-[9px] px-1.5 py-px min-w-[18px]',
      thumbTop: 3,
    },
    lg: {
      pad: 'py-1.5 px-3 sm:py-2 sm:px-5',
      text: 'text-[11px] sm:text-xs tracking-wide',
      icon: 'h-3 w-3 sm:h-3.5 sm:w-3.5',
      gap: 'gap-1.5 sm:gap-2',
      track: 'p-1',
      badge: 'text-[10px] px-2 py-px min-w-[20px]',
      thumbTop: 4,
    },
  }[size];

  const textSize = ({ xs: '2xs', sm: 'xs', md: 'sm', lg: 'md' } as const)[size];
  const textTracking = (
    { xs: 'widest', sm: 'wider', md: 'wide', lg: 'wide' } as const
  )[size];
  const badgeSize = ({ xs: '2xs', sm: '2xs', md: 'xs', lg: 'xs' } as const)[
    size
  ];

  /* ── shape tokens ─────────────────────────────────────────────────────── */
  const shapeClass = {
    pill: 'rounded-full',
    rounded: 'rounded-2xl',
    soft: 'rounded-xl',
    square: 'rounded-lg',
  }[shape];

  /* ── thumb fill ───────────────────────────────────────────────────────── */
  const thumbStyle: React.CSSProperties = (() => {
    switch (variant) {
      case 'solid':
        return {
          background: colors.primary,
          boxShadow: `0 1px 4px ${colors.primary}55, 0 4px 16px ${colors.primary}28`,
        };
      case 'white':
        return {
          background: isDark ? 'rgba(255,255,255,0.10)' : APP_HEX.white,
          boxShadow: isDark
            ? '0 1px 4px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)'
            : '0 1px 4px rgba(0,0,0,0.10), 0 2px 12px rgba(0,0,0,0.06)',
        };
      case 'outline':
        return {
          background: 'transparent',
          boxShadow: `inset 0 0 0 1.5px ${colors.primary}`,
        };
      default: // gradient
        return {
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 60%, ${colors.tertiary} 100%)`,
          boxShadow: `0 2px 6px ${colors.primary}55, 0 6px 20px ${colors.primary}28`,
        };
    }
  })();

  const activeTextColor =
    variant === 'white'
      ? isDark
        ? 'text-white'
        : 'text-gray-900'
      : variant === 'outline'
        ? 'text-[var(--color-primary)]'
        : 'text-white';

  /* ── hover ghost rect position ────────────────────────────────────────── */
  const ghostStyle = (): React.CSSProperties => {
    if (hovered === null || hovered === activeIndex) return {};
    const btn = itemRefs.current[hovered];
    const container = containerRef.current;
    if (!btn || !container) return {};
    const cr = container.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    return {
      left: br.left - cr.left,
      width: br.width,
      height: br.height,
      top: sizeMap.thumbTop,
    };
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId ?? ''}
      role="radiogroup"
      aria-label="Segmented control"
      aria-disabled={disabled}
      className={[
        'relative inline-flex items-center',
        'bg-gray-100/80 dark:bg-white/[0.05]',
        'border border-gray-200/70 dark:border-white/[0.07]',
        'shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)]',
        'max-w-full min-w-0 overflow-x-auto no-scrollbar',
        shapeClass,
        sizeMap.track,
        fullWidth ? 'w-full' : 'w-fit',
        disabled ? 'opacity-50 pointer-events-none' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* ── animated sliding thumb ──────────────────────────────────────── */}
      {activeIndex >= 0 && (
        <span
          aria-hidden="true"
          className={[
            'absolute z-0 pointer-events-none',
            shapeClass,
            mounted
              ? 'transition-[left,width,height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ ...thumb, top: sizeMap.thumbTop, ...thumbStyle }}
        />
      )}

      {/* ── hover ghost highlight ────────────────────────────────────────── */}
      {hovered !== null && hovered !== activeIndex && (
        <span
          aria-hidden="true"
          className={[
            'absolute z-0 pointer-events-none',
            shapeClass,
            'transition-opacity duration-150',
          ].join(' ')}
          style={{
            ...ghostStyle(),
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          }}
        />
      )}

      {/* ── option buttons ──────────────────────────────────────────────── */}
      {options.map((opt, i) => {
        const isActive = opt.value === value;
        const isDisabled = disabled || opt.disabled;
        const OptionIcon = opt.icon;

        return (
          <button
            key={String(opt.value)}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-disabled={isDisabled}
            title={opt.iconOnly ? opt.label : undefined}
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(opt.value)}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            className={[
              'relative z-10 inline-flex items-center justify-center whitespace-nowrap select-none',
              'max-w-full min-w-fit',
              'font-semibold uppercase',
              'transition-colors duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1',
              shapeClass,
              sizeMap.gap,
              sizeMap.pad,
              sizeMap.text,
              fullWidth ? 'flex-1' : '',
              isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
              isActive
                ? activeTextColor
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid={`${testId}-option-${String(i)}`}
          >
            {OptionIcon && (
              <OptionIcon
                className={[
                  'shrink-0 transition-all duration-200',
                  sizeMap.icon,
                  isActive
                    ? 'scale-110 drop-shadow-sm'
                    : 'scale-100 opacity-70',
                ].join(' ')}
              />
            )}

            {!opt.iconOnly && (
              <Text
                as="span"
                size={textSize}
                weight="semibold"
                color="inherit"
                tracking={textTracking}
                leading="none"
              >
                {opt.label}
              </Text>
            )}

            {opt.badge !== undefined && (
              <span
                className={[
                  'inline-flex items-center justify-center rounded-full font-bold leading-none tabular-nums',
                  sizeMap.badge,
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-300/60 dark:bg-white/10 text-gray-600 dark:text-gray-300',
                ].join(' ')}
              >
                <Text
                  as="span"
                  size={badgeSize}
                  weight="bold"
                  color="inherit"
                  leading="none"
                >
                  {opt.badge}
                </Text>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
